(() => {
  const VERSION = 'reference-v6-perf';
  const previous = window.__cpCartDrawerEnhancer;
  if (previous?.version === VERSION) {
    previous.schedule();
    return;
  }

  let hydrateTimer = null;
  let requestToken = 0;
  let initialCartState = null;
  let hasHydrated = false;
  let intentBound = false;

  const getConfig = () => document.querySelector('[data-cp-cart-drawer-config]');
  const getDrawer = () => document.querySelector('cart-drawer');

  const getCart = async () => {
    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
    const response = await fetch(cartUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const setText = (element, value) => {
    if (element && typeof value === 'string' && value.length) element.textContent = value;
  };

  const ensureSlot = (drawer, type) => {
    const inner = drawer.querySelector('.drawer__inner');
    if (!inner) return null;

    if (type === 'upsell') {
      let slot = inner.querySelector('[data-cp-cart-upsell-slot]');
      if (!slot) {
        const footer = inner.querySelector('.drawer__footer');
        if (!footer) return null;
        slot = document.createElement('div');
        slot.className = 'cp-cart-upsell-slot';
        slot.setAttribute('data-cp-cart-upsell-slot', '');
        footer.insertAdjacentElement('beforebegin', slot);
      }
      return slot;
    }

    let slot = inner.querySelector('[data-cp-cart-trust-slot]');
    if (!slot) {
      const footer = inner.querySelector('.drawer__footer');
      const ctas = footer?.querySelector('.cart__ctas');
      if (!footer || !ctas) return null;
      slot = document.createElement('div');
      slot.className = 'cp-cart-trust-slot';
      slot.setAttribute('data-cp-cart-trust-slot', '');
      ctas.insertAdjacentElement('beforebegin', slot);
    }
    return slot;
  };

  const decorateHeader = (config, drawer, cart) => {
    const heading = drawer.querySelector('.drawer__header .drawer__heading');
    if (!heading) return;

    const label = config.dataset.cartHeading || 'Your cart';
    heading.replaceChildren(document.createTextNode(label));

    const count = document.createElement('span');
    count.className = 'cp-cart-drawer__count';
    count.textContent = `(${Number(cart?.item_count || 0)})`;
    heading.append(count);
  };

  const decorateEmptyState = (config, drawer, cart) => {
    const content = drawer.querySelector('.cart-drawer__empty-content');
    if (!content) return;

    const heading = content.querySelector('.cart__empty-text');
    if (!heading) return;

    setText(heading, config.dataset.emptyHeading || 'Your cart is empty.');

    let icon = content.querySelector('.cp-cart-empty__icon');
    if (!icon) {
      icon = document.createElement('div');
      icon.className = 'cp-cart-empty__icon';
      icon.setAttribute('aria-hidden', 'true');
      heading.insertAdjacentElement('beforebegin', icon);
    }
    icon.textContent = String(Number(cart?.item_count || 0));

    let description = content.querySelector('.cp-cart-empty__description');
    if (!description) {
      description = document.createElement('p');
      description.className = 'cp-cart-empty__description';
      heading.insertAdjacentElement('afterend', description);
    }
    description.textContent = config.dataset.emptyText || 'Start with one simple 30-day routine.';

    const button = content.querySelector('a.button');
    if (button) {
      button.classList.add('cp-cart-empty__button');
      setText(button, config.dataset.emptyButtonLabel || 'Shop ChloroPill');
      if (config.dataset.emptyButtonLink) button.href = config.dataset.emptyButtonLink;
    }
  };

  const decorateCheckout = (config, drawer) => {
    const checkout = drawer.querySelector('#CartDrawer-Checkout');
    if (!checkout) return;
    setText(checkout, config.dataset.checkoutLabel || 'Continue to checkout');
  };

  const decorateCartItems = (_config, drawer) => {
    drawer.querySelectorAll('.cart-item').forEach((item) => {
      item.classList.add('cp-cart-item--native');

      // Remove the old custom quantity label if it was injected by an earlier enhancer.
      item.querySelectorAll('.cp-cart-item__quantity-label').forEach((label) => label.remove());

      // Keep Dawn's remove button in its native quantity wrapper.
      const quantityWrapper = item.querySelector('.cart-item__quantity-wrapper');
      const remove = item.querySelector('cart-remove-button');
      if (quantityWrapper && remove && !quantityWrapper.contains(remove)) {
        quantityWrapper.append(remove);
      }

      const removeButton = remove?.querySelector('button');
      if (removeButton) {
        removeButton.classList.remove('cp-cart-item__remove');

        // If an older script replaced Dawn's icon with the word "Remove", restore the native icon.
        if (!removeButton.querySelector('.svg-wrapper')) {
          removeButton.innerHTML = `
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"/>
                <path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"/>
              </svg>
            </span>
          `;
        }
      }
    });
  };

  const renderTrust = (config, drawer) => {
    const existingSlot = drawer.querySelector('[data-cp-cart-trust-slot]');
    if (config.dataset.trustEnabled !== 'true') {
      existingSlot?.remove();
      return;
    }

    const template = config.querySelector('[data-cp-cart-trust-template]');
    if (!template?.content) {
      existingSlot?.remove();
      return;
    }

    const content = template.content.cloneNode(true);
    if (!content.querySelector('.cp-cart-trust__item')) {
      existingSlot?.remove();
      return;
    }

    const slot = ensureSlot(drawer, 'trust');
    if (!slot) return;
    slot.replaceChildren(content);
  };

  const renderUpsell = (config, drawer, cart) => {
    const existingSlot = drawer.querySelector('[data-cp-cart-upsell-slot]');
    if (config.dataset.upsellEnabled !== 'true') {
      existingSlot?.remove();
      return;
    }

    const template = config.querySelector('[data-cp-cart-upsell-template]');
    if (!template?.content) {
      existingSlot?.remove();
      return;
    }

    const cartProductIds = new Set((cart?.items || []).map((item) => String(item.product_id)));
    const content = template.content.cloneNode(true);
    const candidates = Array.from(content.querySelectorAll('[data-cp-cart-upsell-candidate]'));
    const availableCandidates = candidates.filter((item) => item.dataset.available === 'true');

    // Prefer an available product that is not already in the cart. If every
    // selected product is already in the cart or sold out, still render the
    // configured product/collection item instead of hiding the upsell area.
    const candidate =
      availableCandidates.find((item) => !cartProductIds.has(String(item.dataset.productId))) ||
      candidates.find((item) => !cartProductIds.has(String(item.dataset.productId))) ||
      availableCandidates[0] ||
      candidates[0];

    if (!candidate) {
      existingSlot?.remove();
      return;
    }

    const candidateContainer = content.querySelector('.cp-cart-upsell__candidates');
    if (!candidateContainer) {
      existingSlot?.remove();
      return;
    }

    candidateContainer.replaceChildren(candidate);
    const slot = ensureSlot(drawer, 'upsell');
    if (!slot) return;
    slot.replaceChildren(content);
  };

  const readInitialCart = () => {
    if (initialCartState) return initialCartState;

    const node = getConfig()?.querySelector('[data-cp-cart-initial-state]');
    try {
      initialCartState = JSON.parse(node?.textContent || '{"item_count":0,"items":[]}');
    } catch (error) {
      initialCartState = { item_count: 0, items: [] };
    }

    return initialCartState;
  };

  const applyCart = (cart) => {
    const config = getConfig();
    const drawer = getDrawer();

    if (!config || !drawer) {
      drawer?.querySelector('[data-cp-cart-upsell-slot]')?.remove();
      drawer?.querySelector('[data-cp-cart-trust-slot]')?.remove();
      return;
    }

    decorateHeader(config, drawer, cart);
    decorateEmptyState(config, drawer, cart);
    decorateCheckout(config, drawer);
    decorateCartItems(config, drawer);
    renderTrust(config, drawer);
    renderUpsell(config, drawer, cart);
    hasHydrated = true;
  };

  const hydrateInitial = () => {
    if (hasHydrated) return;
    applyCart(readInitialCart());
  };

  const hydrateFromServer = async () => {
    const token = ++requestToken;
    let cart;

    try {
      cart = await getCart();
    } catch (error) {
      return;
    }

    if (token !== requestToken) return;
    applyCart(cart);
  };

  const schedule = () => {
    window.clearTimeout(hydrateTimer);
    hydrateTimer = window.setTimeout(hydrateFromServer, 60);
  };

  const onCartIntent = (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('#cart-icon-bubble, .header__icon--cart')
      : null;

    if (!target) return;
    hydrateInitial();
  };

  const initialize = () => {
    if (intentBound) return;
    intentBound = true;
    document.addEventListener('pointerdown', onCartIntent, true);
    document.addEventListener('click', onCartIntent, true);
  };

  window.__cpCartDrawerEnhancer = { version: VERSION, schedule };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', () => {
    initialCartState = null;
    hasHydrated = false;
    initialize();
  });

  document.addEventListener('shopify:section:reorder', () => {
    initialCartState = null;
    hasHydrated = false;
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      schedule();
    });
  }
})();
